import React, { useEffect, useState, useRef } from "react";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  Box,
  styled,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { HttpService } from "../../service/HttpService";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import { useAuth } from "../../context/AuthProvider";
import dayjs from "dayjs";
import { MdClose } from "react-icons/md";
import { CiEdit } from "react-icons/ci";
import { toast } from "react-toastify";
import moment from "moment";
import "./Harvest.scss";
import HarvestReport from "./HarvestReport";

const formatFloat = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return 0;
  return num.toFixed(2);
};

const getWeekOfYear = (date) => {
  const d = new Date(date);
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(
    ((d - yearStart) / 86400000 + yearStart.getDay() + 1) / 7,
  );
  return weekNumber;
};

const HarvestArchive = ({ roomId, harvestedDate }) => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);
  const [loading, setLoading] = useState(false);
  const [archiveData, setArchiveData] = useState([]);
  const [editingHarvestId, setEditingHarvestId] = useState(null);
  const [editHarvestValues, setEditHarvestValues] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [cultivars, setCultivars] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [priceData, setPriceData] = useState([]);

  // Fresh Frozen states
  const [frozenArchiveData, setFrozenArchiveData] = useState([]);
  const [isFrozenLoading, setIsFrozenLoading] = useState(false);
  const [editingFrozenHarvestId, setEditingFrozenHarvestId] = useState(null);
  const [editFrozenHarvestValues, setEditFrozenHarvestValues] = useState({});

  const editHarvestValuesRef = useRef(editHarvestValues);
  const editingHarvestIdRef = useRef(editingHarvestId);
  const editFrozenHarvestValuesRef = useRef(editFrozenHarvestValues);
  const editingFrozenHarvestIdRef = useRef(editingFrozenHarvestId);

  useEffect(() => {
    editHarvestValuesRef.current = editHarvestValues;
  }, [editHarvestValues]);

  useEffect(() => {
    editingHarvestIdRef.current = editingHarvestId;
  }, [editingHarvestId]);

  useEffect(() => {
    editFrozenHarvestValuesRef.current = editFrozenHarvestValues;
  }, [editFrozenHarvestValues]);

  useEffect(() => {
    editingFrozenHarvestIdRef.current = editingFrozenHarvestId;
  }, [editingFrozenHarvestId]);

  const fetchArchivedData = async () => {
    setLoading(true);
    try {
      let endpoint = "harvest/archived";
      const params = [];

      if (roomId) {
        params.push(`roomId=${roomId}`);
      }

      if (harvestedDate) {
        params.push(`harvestedDate=${harvestedDate}`);
      }
      if (params.length > 0) {
        endpoint += `?${params.join("&")}`;
      }

      const response = await httpService.get(endpoint);
      if (response?.data) {
        setArchiveData(response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFrozenArchivedData = async () => {
    setIsFrozenLoading(true);
    try {
      let endpoint = "harvest/fresh-frozen/archived";
      const params = [];

      if (roomId) params.push(`roomId=${roomId}`);
      if (harvestedDate) params.push(`harvestedDate=${harvestedDate}`);

      if (params.length > 0) {
        endpoint += `?${params.join("&")}`;
      }

      const response = await httpService.get(endpoint);
      if (response?.data) {
        setFrozenArchiveData(response.data);
      }
    } catch (error) {
      setFrozenArchiveData([]);
    } finally {
      setIsFrozenLoading(false);
    }
  };

  const fetchPriceConfig = async () => {
    try {
      const response = await httpService.get("harvest/price-config");
      if (response?.data) {
        setPriceData([{ ...response.data }]);
      }
    } catch (error) {
      setPriceData([]);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [cultivarRes, roomRes] = await Promise.all([
        httpService.get("plant/get_plant"),
        httpService.get("rooms"),
      ]);
      setCultivars(cultivarRes?.data?.data || []);
      setRooms(roomRes?.data?.data || []);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  useEffect(() => {
    fetchArchivedData();
    fetchFrozenArchivedData();
    fetchPriceConfig();
    fetchDropdownData();
  }, [roomId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const currentId = editingHarvestIdRef.current;
      const currentValues = editHarvestValuesRef.current;
      const currentFrozenId = editingFrozenHarvestIdRef.current;
      const currentFrozenValues = editFrozenHarvestValuesRef.current;

      const isPopover = !!event.target.closest(
        ".MuiPopover-root, .MuiDialog-root, .MuiPickersPopper-root, .MuiPickersLayout-root",
      );
      if (isPopover) return;

      if (!document.body.contains(event.target)) return;

      const scrollContainer = event.target.closest(
        ".MuiDataGrid-virtualScroller",
      );
      if (scrollContainer) {
        const rect = scrollContainer.getBoundingClientRect();
        if (
          event.clientX >= rect.right - 20 ||
          event.clientY >= rect.bottom - 20
        ) {
          return;
        }
      }

      if (
        event.target.closest(
          ".MuiDataGrid-columnHeaders, .MuiDataGrid-footerContainer, .MuiDataGrid-overlay",
        )
      ) {
        return;
      }

      // Handle standard harvest table click outside
      if (currentId && currentValues && Object.keys(currentValues).length > 0) {
        const isInsideEditing = event.target.closest(
          `.MuiDataGrid-row[data-id="${currentId}"], input, textarea, select, .MuiSelect-select`,
        );
        if (!isInsideEditing) {
          handleSaveHarvest(currentValues);
        }
      }

      // Handle frozen harvest table click outside
      if (
        currentFrozenId &&
        currentFrozenValues &&
        Object.keys(currentFrozenValues).length > 0
      ) {
        const isInsideFrozen = event.target.closest(
          `.MuiDataGrid-row[data-id="${currentFrozenId}"], input, textarea, select, .MuiSelect-select`,
        );
        if (!isInsideFrozen) {
          handleSaveFrozenHarvest(currentFrozenValues);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const handleEditHarvest = async (row) => {
    setEditingHarvestId(row.id);
    setEditHarvestValues({ ...row });
    if (row.harvest_date) {
      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${moment(row.harvest_date).format("YYYY-MM-DD")}`,
        );
        if (
          response?.data?.rooms?.length > 0 ||
          response?.data?.cultivars?.length > 0
        ) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
      }
    }
  };

  const handleCancelHarvest = () => {
    setEditingHarvestId(null);
    setEditHarvestValues({});
    setRooms([]);
    setCultivars([]);
    fetchArchivedData();
  };

  const handleSaveHarvest = async (valuesToSave) => {
    setLoading(true);
    try {
      const currentValues = valuesToSave || editHarvestValuesRef.current;
      const currentId = editingHarvestIdRef.current;

      if (!currentId) return;

      const payload = {
        price_config_id: currentValues.price_config_id || "",
        week_of_harvest: parseInt(currentValues.week_of_harvest) || 0,
        harvest_date: dayjs(currentValues.harvest_date).format("YYYY-MM-DD"),
        cultivar_id: parseInt(currentValues.cultivar_id) || 0,
        room_id: parseInt(currentValues.room_id) || 0,
        plants: parseInt(currentValues.plants) || 0,
        wet_weight: parseFloat(currentValues.wet_weight) || 0,
        waste: parseFloat(currentValues.waste) || 0,
        weight_after_bucking:
          parseFloat(currentValues.weight_after_bucking) || 0,
        bulk_lbs: parseFloat(currentValues.bulk_lbs) || 0,
        smalls_lbs: parseFloat(currentValues.smalls_lbs) || 0,
        trim_lbs: parseFloat(currentValues.trim_lbs) || 0,
      };

      await httpService.put(`harvest/${currentId}`, payload);
      toast.success("Harvest updated successfully");
      setEditingHarvestId(null);
      setEditHarvestValues({});
      fetchArchivedData();
    } catch (error) {
      toast.error("Failed to update harvest");
    } finally {
      setLoading(false);
    }
  };

  const handleHarvestFieldChange = async (field, value) => {
    let updatedValues = { ...editHarvestValuesRef.current, [field]: value };

    if (field === "harvest_date" && value) {
      const weekNumber = getWeekOfYear(value);
      updatedValues = { ...updatedValues, week_of_harvest: weekNumber };

      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${dayjs(value).format("YYYY-MM-DD")}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
          updatedValues = {
            ...updatedValues,
            room_id: null,
            cultivar_id: null,
          };
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
      }
    }

    if (
      (field === "cultivar_id" || field === "room_id") &&
      updatedValues.cultivar_id &&
      updatedValues.room_id &&
      updatedValues.harvest_date
    ) {
      try {
        const response = await httpService.get(
          `harvest/plant-count?plantId=${updatedValues.cultivar_id}&roomId=${updatedValues.room_id}&harvestedDate=${dayjs(updatedValues.harvest_date).format("YYYY-MM-DD")}`,
        );
        const count =
          response?.data?.count ?? response?.data?.data ?? response?.data;
        if (count !== undefined && count !== null) {
          updatedValues = { ...updatedValues, plants: parseInt(count) || 0 };
        }
      } catch (error) {
        console.error("Error fetching plant count:", error);
      }
    }

    setEditHarvestValues(updatedValues);
  };

  const handleMenuClickFrozen = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(`frozen_${rowId}`);
  };

  const handleEditFrozenHarvest = async (row) => {
    setEditingFrozenHarvestId(row.id);
    setEditFrozenHarvestValues({ ...row });
    if (row.harvest_date) {
      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${moment(row.harvest_date).format("YYYY-MM-DD")}`,
        );
        if (
          response?.data?.rooms?.length > 0 ||
          response?.data?.cultivars?.length > 0
        ) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
      }
    }
  };

  const handleCancelFrozenHarvest = () => {
    setEditingFrozenHarvestId(null);
    setEditFrozenHarvestValues({});
    setRooms([]);
    setCultivars([]);
    fetchFrozenArchivedData();
  };

  const handleSaveFrozenHarvest = async (valuesToSave) => {
    setLoading(true);
    try {
      const currentValues = valuesToSave || editFrozenHarvestValuesRef.current;
      const currentId = editingFrozenHarvestIdRef.current;

      if (!currentId) return;

      const payload = {
        week_of_harvest: parseInt(currentValues.week_of_harvest) || 0,
        harvest_date: dayjs(currentValues.harvest_date).format("YYYY-MM-DD"),
        cultivar_id: parseInt(currentValues.cultivar_id) || 0,
        room_id: parseInt(currentValues.room_id) || 0,
        plants: parseInt(currentValues.plants) || 0,
        wet_weight: parseFloat(currentValues.wet_weight) || 0,
        waste: parseFloat(currentValues.waste) || 0,
        fresh_frozen_grams: parseFloat(currentValues.fresh_frozen_grams) || 0,
        rosin_grams: parseFloat(currentValues.rosin_grams) || 0,
        yield: parseFloat(currentValues.yield) || 0,
      };

      await httpService.put(`harvest/fresh-frozen/${currentId}`, payload);
      toast.success("Fresh Frozen Harvest updated successfully");
      setEditingFrozenHarvestId(null);
      setEditFrozenHarvestValues({});
      fetchFrozenArchivedData();
    } catch (error) {
      toast.error("Failed to update fresh frozen harvest");
    } finally {
      setLoading(false);
    }
  };

  const handleFrozenHarvestFieldChange = async (field, value) => {
    let updatedValues = {
      ...editFrozenHarvestValuesRef.current,
      [field]: value,
    };

    if (field === "harvest_date" && value) {
      const weekNumber = getWeekOfYear(value);
      updatedValues = { ...updatedValues, week_of_harvest: weekNumber };

      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${dayjs(value).format("YYYY-MM-DD")}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
          updatedValues = {
            ...updatedValues,
            room_id: null,
            cultivar_id: null,
          };
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
      }
    }

    if (
      (field === "cultivar_id" || field === "room_id") &&
      updatedValues.cultivar_id &&
      updatedValues.room_id &&
      updatedValues.harvest_date
    ) {
      try {
        const response = await httpService.get(
          `harvest/plant-count?plantId=${updatedValues.cultivar_id}&roomId=${updatedValues.room_id}&harvestedDate=${dayjs(updatedValues.harvest_date).format("YYYY-MM-DD")}`,
        );
        const count =
          response?.data?.count ?? response?.data?.data ?? response?.data;
        if (count !== undefined && count !== null) {
          updatedValues = { ...updatedValues, plants: parseInt(count) || 0 };
        }
      } catch (error) {
        console.error("Error fetching plant count:", error);
      }
    }

    setEditFrozenHarvestValues(updatedValues);
  };

  const StyledGridOverlay = styled("div")(() => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  }));

  const CustomNoRowsOverlay = () => (
    <StyledGridOverlay>
      <Box sx={{ mt: 1 }}>No Data Found</Box>
    </StyledGridOverlay>
  );

  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <>
          {editingHarvestId === params.row.id ? (
            <IconButton
              onClick={handleCancelHarvest}
              color="error"
              size="small"
            >
              <MdClose />
            </IconButton>
          ) : (
            <>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuClick(e, params.row.id);
                }}
                size="small"
              >
                <GridMoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && menuRowId === params.row.id}
                onClose={handleCloseMenu}
              >
                <MenuItem
                  onClick={() => {
                    handleEditHarvest(params.row);
                    handleCloseMenu();
                  }}
                >
                  <CiEdit style={{ marginRight: 8 }} /> Edit
                </MenuItem>
              </Menu>
            </>
          )}
        </>
      ),
    },
    {
      field: "week_of_harvest",
      headerName: "Week of Harvest",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingHarvestId === params.row.id ? (
          <input
            type="number"
            value={editHarvestValues.week_of_harvest || ""}
            onChange={(e) =>
              handleHarvestFieldChange("week_of_harvest", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          params.value
        ),
    },
    {
      field: "harvest_date",
      headerName: "Harvest Date",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingHarvestId === params.row.id ? (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                editHarvestValues.harvest_date
                  ? dayjs(editHarvestValues.harvest_date)
                  : null
              }
              onChange={(newValue) =>
                handleHarvestFieldChange("harvest_date", newValue)
              }
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              disableFuture
            />
          </LocalizationProvider>
        ) : params.row.harvest_date ? (
          dayjs(params.row.harvest_date).format("MM-DD-YYYY")
        ) : (
          "-"
        ),
    },
    {
      field: "cultivar_name",
      headerName: "Cultivar",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingHarvestId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editHarvestValues.cultivar_id || ""}
              onChange={(e) =>
                handleHarvestFieldChange("cultivar_id", e.target.value)
              }
            >
              {cultivars.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          params.value
        ),
    },
    {
      field: "room_name",
      headerName: "Room",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingHarvestId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editHarvestValues.room_id || ""}
              onChange={(e) =>
                handleHarvestFieldChange("room_id", e.target.value)
              }
            >
              {rooms.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          params.value
        ),
    },
    {
      field: "plants",
      headerName: "Plants",
      flex: 1,
      minWidth: 100,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingHarvestId === params.row.id ? (
          <input
            type="number"
            value={editHarvestValues.plants || 0}
            onChange={(e) => handleHarvestFieldChange("plants", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.row.plants)
        ),
    },
    {
      field: "wet_weight",
      headerName: "Wet Weight",
      flex: 1,
      minWidth: 120,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingHarvestId === params.row.id ? (
          <input
            type="number"
            value={editHarvestValues.wet_weight || 0}
            onChange={(e) =>
              handleHarvestFieldChange("wet_weight", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.row.wet_weight)
        ),
    },
    {
      field: "waste",
      headerName: "Waste",
      flex: 1,
      minWidth: 100,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingHarvestId === params.row.id ? (
          <input
            type="number"
            value={editHarvestValues.waste || 0}
            onChange={(e) => handleHarvestFieldChange("waste", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.row.waste)
        ),
    },
    {
      field: "total_wet_weight_after_waste",
      headerName: "Total Wet Weight After Waste",
      flex: 1,
      minWidth: 200,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      valueGetter: (params) =>
        formatFloat(params.row.total_wet_weight_after_waste),
    },
    // {
    //   field: "weight_after_bucking",
    //   headerName: "Weight After Bucking",
    //   flex: 1,
    //   minWidth: 180,
    //   headerClassName: "headerclass",
    //   headerAlign: "top",
    //   align: "top",
    //   renderCell: (params) =>
    //     editingHarvestId === params.row.id ? (
    //       <input
    //         type="number"
    //         value={editHarvestValues.weight_after_bucking || 0}
    //         onChange={(e) =>
    //           handleHarvestFieldChange("weight_after_bucking", e.target.value)
    //         }
    //         style={{
    //           width: "100%",
    //           padding: "8px",
    //           border: "1px solid #ccc",
    //           borderRadius: "4px",
    //         }}
    //       />
    //     ) : (
    //       formatFloat(params.row.weight_after_bucking)
    //     ),
    // },
    // {
    //   field: "weight_lost_after_bucking",
    //   headerName: "Weight lost after bucking",
    //   flex: 1,
    //   minWidth: 200,
    //   headerClassName: "headerclass",
    //   headerAlign: "top",
    //   align: "top",
    //   valueGetter: (params) =>
    //     formatFloat(params.row.weight_lost_after_bucking),
    // },
    {
      field: "total_packaged",
      headerName: "TOTAL PACKAGED",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => formatFloat(params.row.total_packaged),
    },
    {
      field: "bulk_lbs",
      headerName: "Bulk in POUNDS",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingHarvestId === params.row.id ? (
          <input
            type="number"
            value={editHarvestValues.bulk_lbs || 0}
            onChange={(e) =>
              handleHarvestFieldChange("bulk_lbs", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.row.bulk_lbs)
        ),
    },
    {
      field: "smalls_lbs",
      headerName: "Smalls in POUNDS",
      flex: 1,
      minWidth: 160,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingHarvestId === params.row.id ? (
          <input
            type="number"
            value={editHarvestValues.smalls_lbs || 0}
            onChange={(e) =>
              handleHarvestFieldChange("smalls_lbs", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.row.smalls_lbs)
        ),
    },
    {
      field: "trim_lbs",
      headerName: "Trim in POUNDS",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingHarvestId === params.row.id ? (
          <input
            type="number"
            value={editHarvestValues.trim_lbs || 0}
            onChange={(e) =>
              handleHarvestFieldChange("trim_lbs", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.row.trim_lbs)
        ),
    },
    {
      field: "total_lbs_per_plant_avg",
      headerName: "TOTAL lbs per PLANT Average",
      flex: 1,
      minWidth: 200,
      headerClassName: "headerclass",
      valueGetter: (params) => formatFloat(params.row.total_lbs_per_plant_avg),
    },
    {
      field: "bulk_lbs_per_plant_avg",
      headerName: "BULK lbs per PLANT Average",
      flex: 1,
      minWidth: 200,
      headerClassName: "headerclass",
      valueGetter: (params) => formatFloat(params.row.bulk_lbs_per_plant_avg),
    },
    {
      field: "smalls_lbs_per_plant_avg",
      headerName: "SMALLS lbs per PLANT Average",
      flex: 1,
      minWidth: 220,
      headerClassName: "headerclass",
      valueGetter: (params) => formatFloat(params.row.smalls_lbs_per_plant_avg),
    },
    {
      field: "trim_lbs_per_plant_avg",
      headerName: "TRIM lbs per PLANT Average",
      flex: 1,
      minWidth: 200,
      headerClassName: "headerclass",
      valueGetter: (params) => formatFloat(params.row.trim_lbs_per_plant_avg),
    },
    {
      field: "total_value_per_plant_avg",
      headerName: "TOTAL VALUE per PLANT Average",
      flex: 1,
      minWidth: 220,
      headerClassName: "headerclass",
      valueGetter: (params) =>
        "$" + formatFloat(params.row.total_value_per_plant_avg),
    },
    {
      field: "bulk_value_per_plant_avg",
      headerName: "BULK VALUE per PLANT Average",
      flex: 1,
      minWidth: 220,
      headerClassName: "headerclass",
      valueGetter: (params) =>
        "$" + formatFloat(params.row.bulk_value_per_plant_avg),
    },
    {
      field: "smalls_value_per_plant_avg",
      headerName: "SMALLS VALUE per PLANT Average",
      flex: 1,
      minWidth: 240,
      headerClassName: "headerclass",
      valueGetter: (params) =>
        "$" + formatFloat(params.row.smalls_value_per_plant_avg),
    },
    {
      field: "trim_value_per_plant_avg",
      headerName: "TRIM VALUE per PLANT Average",
      flex: 1,
      minWidth: 220,
      headerClassName: "headerclass",
      valueGetter: (params) =>
        "$" + formatFloat(params.row.trim_value_per_plant_avg),
    },
    {
      field: "bulk_percent_per_plant_avg",
      headerName: "% BULK per PLANT Average",
      flex: 1,
      minWidth: 200,
      headerClassName: "headerclass",
      valueGetter: (params) =>
        params.row.bulk_percent_per_plant_avg
          ? (parseFloat(params.row.bulk_percent_per_plant_avg) * 100).toFixed(
              2,
            ) + "%"
          : "0%",
    },
    {
      field: "smalls_percent_per_plant_avg",
      headerName: "% SMALLS per PLANT Average",
      flex: 1,
      minWidth: 220,
      headerClassName: "headerclass",
      valueGetter: (params) =>
        params.row.smalls_percent_per_plant_avg
          ? (parseFloat(params.row.smalls_percent_per_plant_avg) * 100).toFixed(
              2,
            ) + "%"
          : "0%",
    },
    {
      field: "trim_percent_per_plant_avg",
      headerName: "% TRIM per PLANT Average",
      flex: 1,
      minWidth: 200,
      headerClassName: "headerclass",
      valueGetter: (params) =>
        params.row.trim_percent_per_plant_avg
          ? (parseFloat(params.row.trim_percent_per_plant_avg) * 100).toFixed(
              2,
            ) + "%"
          : "0%",
    },
  ];

  const frozenHarvestColumns = [
    {
      field: "action",
      headerName: "Action",
      width: 100,
      align: "left",
      headerAlign: "left",
      sortable: false,
      renderCell: (params) => (
        <>
          {editingFrozenHarvestId === params.row.id ? (
            <IconButton
              onClick={handleCancelFrozenHarvest}
              color="error"
              size="small"
            >
              <MdClose />
            </IconButton>
          ) : (
            <>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuClickFrozen(e, params.row.id);
                }}
                size="small"
              >
                <GridMoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={
                  Boolean(anchorEl) && menuRowId === `frozen_${params.row.id}`
                }
                onClose={handleCloseMenu}
              >
                <MenuItem
                  onClick={() => {
                    handleEditFrozenHarvest(params.row);
                    handleCloseMenu();
                  }}
                >
                  <CiEdit style={{ marginRight: 8 }} /> Edit
                </MenuItem>
              </Menu>
            </>
          )}
        </>
      ),
    },
    {
      field: "week_of_harvest",
      headerName: "Week of Harvest",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingFrozenHarvestId === params.row.id ? (
          <input
            type="number"
            value={editFrozenHarvestValues.week_of_harvest || ""}
            onChange={(e) =>
              handleFrozenHarvestFieldChange("week_of_harvest", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "harvest_date",
      headerName: "Harvest Date",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingFrozenHarvestId === params.row.id ? (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                editFrozenHarvestValues.harvest_date
                  ? dayjs(editFrozenHarvestValues.harvest_date)
                  : null
              }
              onChange={(newValue) =>
                handleFrozenHarvestFieldChange("harvest_date", newValue)
              }
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              disableFuture
            />
          </LocalizationProvider>
        ) : params.value ? (
          dayjs(params.value).format("MM-DD-YYYY")
        ) : (
          "-"
        ),
    },
    {
      field: "cultivar_name",
      headerName: "Cultivar",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingFrozenHarvestId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editFrozenHarvestValues.cultivar_id || ""}
              onChange={(e) =>
                handleFrozenHarvestFieldChange("cultivar_id", e.target.value)
              }
            >
              <MenuItem value="">Select Cultivar</MenuItem>
              {cultivars.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "room_name",
      headerName: "Room",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingFrozenHarvestId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editFrozenHarvestValues.room_id || ""}
              onChange={(e) =>
                handleFrozenHarvestFieldChange("room_id", e.target.value)
              }
            >
              <MenuItem value="">Select Room</MenuItem>
              {rooms.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "plants",
      headerName: "No. of Plants",
      flex: 1,
      minWidth: 120,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingFrozenHarvestId === params.row.id ? (
          <input
            type="number"
            value={editFrozenHarvestValues.plants || ""}
            onChange={(e) =>
              handleFrozenHarvestFieldChange("plants", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.value) || 0
        ),
    },
    {
      field: "wet_weight",
      headerName: "Wet Weight",
      flex: 1,
      minWidth: 120,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingFrozenHarvestId === params.row.id ? (
          <input
            type="number"
            value={editFrozenHarvestValues.wet_weight || ""}
            onChange={(e) =>
              handleFrozenHarvestFieldChange("wet_weight", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.value) || 0
        ),
    },
    {
      field: "waste",
      headerName: "Waste",
      flex: 1,
      minWidth: 100,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      renderCell: (params) =>
        editingFrozenHarvestId === params.row.id ? (
          <input
            type="number"
            value={editFrozenHarvestValues.waste || ""}
            onChange={(e) =>
              handleFrozenHarvestFieldChange("waste", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.value) || 0
        ),
    },
    {
      field: "total_wet_weight_after_waste",
      headerName: "Total Wet Weight After Waste",
      flex: 1,
      minWidth: 200,
      headerClassName: "headerclass",
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => {
        const wetWeight = parseFloat(params.row.wet_weight) || 0;
        const waste = parseFloat(params.row.waste) || 0;
        return formatFloat(wetWeight - waste);
      },
    },
    {
      field: "fresh_frozen_grams",
      headerName: "Fresh Frozen Grams",
      flex: 1,
      minWidth: 160,
      headerClassName: "headerclass",
      renderCell: (params) =>
        editingFrozenHarvestId === params.row.id ? (
          <input
            type="number"
            value={editFrozenHarvestValues.fresh_frozen_grams || ""}
            onChange={(e) =>
              handleFrozenHarvestFieldChange(
                "fresh_frozen_grams",
                e.target.value,
              )
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.value) || 0
        ),
    },
    {
      field: "rosin_grams",
      headerName: "Rosin Grams",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      renderCell: (params) =>
        editingFrozenHarvestId === params.row.id ? (
          <input
            type="number"
            value={editFrozenHarvestValues.rosin_grams || ""}
            onChange={(e) =>
              handleFrozenHarvestFieldChange("rosin_grams", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.value) || 0
        ),
    },
    {
      field: "yield",
      headerName: "Yield",
      flex: 1,
      minWidth: 120,
      headerClassName: "headerclass",
      renderCell: (params) =>
        editingFrozenHarvestId === params.row.id ? (
          <input
            type="number"
            value={
              editFrozenHarvestValues.yield !== null
                ? editFrozenHarvestValues.yield
                : ""
            }
            onChange={(e) =>
              handleFrozenHarvestFieldChange("yield", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          formatFloat(params.value) || 0
        ),
    },
  ];

  return (
    <>
      <div className="harvest">
        {loading && <FullScreenLoader />}

        <div className="page-title pb-0 pt-4">
          <div className="title-wrap">
            <h4>Harvest</h4>
          </div>
        </div>
        <HarvestReport roomId={roomId} />
        <DataGrid
          rows={archiveData}
          columns={columns}
          loading={loading}
          autoHeight={archiveData.length <= 10}
          disableRowSelectionOnClick
          sx={{
            border: "1px solid #E0E0E0",
            maxHeight: 576,
            [`& .${gridClasses.cell}`]: { py: 1 },
          }}
          slots={{ noRowsOverlay: CustomNoRowsOverlay }}
          hideFooter
          showCellVerticalBorder
          showColumnVerticalBorder
        />

        <div className="page-title pb-0 pt-4">
          <div className="title-wrap">
            <h4>Fresh Frozen</h4>
          </div>
        </div>
        <DataGrid
          rows={frozenArchiveData}
          columns={frozenHarvestColumns}
          loading={isFrozenLoading}
          autoHeight={frozenArchiveData.length <= 10}
          disableRowSelectionOnClick
          sx={{
            border: "1px solid #E0E0E0",
            maxHeight: 576,
            [`& .${gridClasses.cell}`]: { py: 1 },
          }}
          slots={{ noRowsOverlay: CustomNoRowsOverlay }}
          hideFooter
          showCellVerticalBorder
          showColumnVerticalBorder
        />
      </div>
    </>
  );
};

export default HarvestArchive;
